import { useState, useRef, useEffect } from 'react';
import { Volume2, VolumeX } from 'lucide-react';

const REELS = [
  { src: '/videos/reel-1-web.mp4', label: 'Featured Build' },
  { src: '/videos/reel-2-web.mp4', label: 'Placeholder 1' },
  { src: '/videos/reel-3-web.mp4', label: 'Placeholder 2' },
];

export default function VideoReels({ variant = 'vertical' }) {
  const [current, setCurrent] = useState(0);
  const [muted, setMuted] = useState(true);
  const [dragging, setDragging] = useState(false);
  const videoRef = useRef(null);
  const dragStart = useRef(0);
  const dragDelta = useRef(0);

  useEffect(() => {
    if (videoRef.current && REELS[current].src) {
      videoRef.current.load();
      videoRef.current.play().catch(() => {});
    }
  }, [current]);

  const total = REELS.length;
  const goTo = (idx) => setCurrent(((idx % total) + total) % total);
  const goNext = () => goTo(current + 1);
  const goPrev = () => goTo(current - 1);

  const handlePointerDown = (e) => {
    setDragging(true);
    dragStart.current = e.clientX || e.touches?.[0]?.clientX || 0;
    dragDelta.current = 0;
  };
  const handlePointerMove = (e) => {
    if (!dragging) return;
    const x = e.clientX || e.touches?.[0]?.clientX || 0;
    dragDelta.current = dragStart.current - x;
  };
  const handlePointerUp = () => {
    if (!dragging) return;
    setDragging(false);
    if (Math.abs(dragDelta.current) > 40) {
      if (dragDelta.current > 0) goNext();
      else goPrev();
    }
  };

  // Tap zone handler — tap left half to go prev, right half to go next
  const handleTapZone = (e, side) => {
    e.stopPropagation();
    if (Math.abs(dragDelta.current) > 10) return; // ignore if it was a drag
    if (side === 'left') goPrev();
    else goNext();
  };

  const isHorizontal = variant === 'horizontal';

  // Horizontal variant: simple single-video view (for mobile)
  if (isHorizontal) {
    return (
      <div
        className="relative rounded-2xl overflow-hidden bg-black select-none aspect-[16/9]"
        onTouchStart={handlePointerDown}
        onTouchMove={handlePointerMove}
        onTouchEnd={handlePointerUp}
        data-testid="video-reels"
      >
        {REELS[current].src ? (
          <video
            ref={videoRef}
            src={REELS[current].src}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay loop muted={muted} playsInline
            data-testid="video-reel-player"
          />
        ) : (
          <div className="absolute inset-0 bg-neutral-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
        {/* Tap zones — horizontal */}
        <div className="absolute inset-0 z-[5] flex">
          <div className="w-1/2 h-full min-h-[100px] cursor-pointer" onClick={(e) => handleTapZone(e, 'left')} data-testid="video-tap-left" />
          <div className="w-1/2 h-full min-h-[100px] cursor-pointer" onClick={(e) => handleTapZone(e, 'right')} data-testid="video-tap-right" />
        </div>
          <div className="absolute top-3 left-0 right-0 flex justify-center gap-1.5 z-[6]">
            {REELS.map((_, idx) => (
              <div key={idx} className={`h-[3px] rounded-full transition-all duration-300 ${idx === current ? 'w-6 bg-white' : 'w-3 bg-white/40'}`} />
            ))}
          </div>
        )}
        <div className="absolute bottom-3 left-4 right-4 z-[6]">
          <p className="text-white/60 text-[9px] tracking-widest uppercase mb-0.5">Mid2Mint</p>
          <p className="text-white text-xs font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{REELS[current].label}</p>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
          className="absolute bottom-3 right-3 z-[6] w-7 h-7 flex items-center justify-center rounded-full bg-black/40 text-white/80 hover:text-white transition-colors"
          data-testid="video-reel-mute"
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Volume2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
        </button>
      </div>
    );
  }

  // Vertical variant: carousel with peeking side cards
  const getIndex = (offset) => ((current + offset) % total + total) % total;

  return (
    <div
      className="relative select-none flex items-center justify-center"
      onMouseDown={handlePointerDown}
      onMouseMove={handlePointerMove}
      onMouseUp={handlePointerUp}
      onMouseLeave={() => dragging && handlePointerUp()}
      onTouchStart={handlePointerDown}
      onTouchMove={handlePointerMove}
      onTouchEnd={handlePointerUp}
      data-testid="video-reels"
      style={{ cursor: dragging ? 'grabbing' : 'grab', width: '520px', height: '480px' }}
    >
      {/* Left peek card */}
      <div
        className="absolute rounded-2xl overflow-hidden bg-neutral-900 transition-all duration-500 ease-out cursor-pointer"
        style={{
          width: '260px',
          height: '400px',
          left: '-20px',
          top: '50%',
          transform: 'translateY(-50%) scale(0.85)',
          opacity: 0.6,
          filter: 'brightness(0.35)',
          zIndex: 1,
        }}
        onClick={goPrev}
      >
        {REELS[getIndex(-1)].src ? (
          <video src={REELS[getIndex(-1)].src} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        ) : (
          <div className="w-full h-full bg-neutral-800" />
        )}
      </div>

      {/* Center active card */}
      <div
        className="relative rounded-2xl overflow-hidden bg-black transition-all duration-500 ease-out"
        style={{
          width: '300px',
          height: '470px',
          zIndex: 5,
        }}
      >
        {REELS[current].src ? (
          <video
            ref={videoRef}
            src={REELS[current].src}
            className="absolute inset-0 w-full h-full object-cover"
            autoPlay loop muted={muted} playsInline
            data-testid="video-reel-player"
          />
        ) : (
          <div className="absolute inset-0 bg-neutral-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/15 pointer-events-none" />
        {/* Tap zones — vertical */}
        <div className="absolute inset-0 z-[5] flex">
          <div className="w-1/2 h-full min-h-[100px] cursor-pointer" onClick={(e) => handleTapZone(e, 'left')} data-testid="video-tap-left" />
          <div className="w-1/2 h-full min-h-[100px] cursor-pointer" onClick={(e) => handleTapZone(e, 'right')} data-testid="video-tap-right" />
        </div>

        {/* Dots */}
        {total > 1 && (
          <div className="absolute top-3 left-0 right-0 flex justify-center gap-1.5 z-[6]">
            {REELS.map((_, idx) => (
              <div key={idx} className={`h-[3px] rounded-full transition-all duration-300 ${idx === current ? 'w-6 bg-white' : 'w-3 bg-white/40'}`} />
            ))}
          </div>
        )}

        {/* Label */}
        <div className="absolute bottom-4 left-4 right-4 z-[6]">
          <p className="text-white/60 text-[10px] tracking-widest uppercase mb-1">Mid2Mint</p>
          <p className="text-white text-sm font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>{REELS[current].label}</p>
        </div>

        {/* Mute */}
        <button
          onClick={(e) => { e.stopPropagation(); setMuted(!muted); }}
          className="absolute bottom-4 right-4 z-[6] w-8 h-8 flex items-center justify-center rounded-full bg-black/40 text-white/80 hover:text-white transition-colors"
          data-testid="video-reel-mute"
        >
          {muted ? <VolumeX className="w-3.5 h-3.5" strokeWidth={1.5} /> : <Volume2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
        </button>
      </div>

      {/* Right peek card */}
      <div
        className="absolute rounded-2xl overflow-hidden bg-neutral-900 transition-all duration-500 ease-out cursor-pointer"
        style={{
          width: '260px',
          height: '400px',
          right: '-20px',
          top: '50%',
          transform: 'translateY(-50%) scale(0.85)',
          opacity: 0.6,
          filter: 'brightness(0.35)',
          zIndex: 1,
        }}
        onClick={goNext}
      >
        {REELS[getIndex(1)].src ? (
          <video src={REELS[getIndex(1)].src} className="w-full h-full object-cover" muted playsInline preload="metadata" />
        ) : (
          <div className="w-full h-full bg-neutral-800" />
        )}
      </div>
    </div>
  );
}
